import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { THEME_ENUM, DEFAULT_THEME } from '@/constants';
import { LOCAL_THEME } from '@/constants/storage';

interface ThemeContextType {
  theme: THEME_ENUM;
  setTheme: (theme: THEME_ENUM) => void;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<THEME_ENUM>(() => {
    // 从localStorage读取初始主题
    const savedTheme = localStorage.getItem(LOCAL_THEME);
    return (savedTheme as THEME_ENUM) || DEFAULT_THEME;
  });

  useEffect(() => {
    // 设置data-theme属性
    document.documentElement.setAttribute('data-theme', theme);

    // 保存到localStorage
    localStorage.setItem(LOCAL_THEME, theme);
  }, [theme]);

  const setTheme = (newTheme: THEME_ENUM) => {
    setThemeState(newTheme);
  };

  const toggleTheme = () => {
    setThemeState(prevTheme =>
      prevTheme === THEME_ENUM.LIGHT ? THEME_ENUM.DARK : THEME_ENUM.LIGHT
    );
  };

  const isDark = theme === THEME_ENUM.DARK;

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
